/** "New task": the control that creates one (F-011, G-Wcontrib-14) - on the work queue and on a record.
 *
 * `POST /tasks` existed and nothing called it, so a Contributor could raise a note and never a task,
 * and a task that stands alone (EXP-24: *chase the landlord*, before there is a record) could not be
 * made from the product at all. One component serves both places so the form, the refusals and the
 * rules cannot differ: pass `recordId` and the task is about that record; omit it and it is unlinked.
 *
 * **Who is shown it.** `POST /tasks` is `MAY_WRITE` (Admin, Approver, Contributor). A Viewer or an
 * Auditor is offered no control and the page says who does (design rule 6): a button that predictably
 * 403s tells a reader they have a permissions problem they cannot fix. While `/auth/me` is in flight
 * the role is unknown and that is **not** "may not": nothing is drawn until it answers.
 *
 * **Who it is for.** The list of members is Admin-only (`GET /members`), so an Admin picks among the
 * active people and defaults to themselves; everyone else who may create a task creates it for
 * themselves, and the form does not show a picker that would fail. Not a limit this screen invented:
 * it is the server's own, and the picker only keeps an Admin from choosing a person the server would
 * refuse (a suspended one).
 *
 * **What is sent** is only what the person chose (`useCreateTask`): a title, an owner and a priority
 * always, a due date and a record when there is one. A refused write keeps the draft and the drawer
 * open and says why; a successful one closes it and the queue and the bell re-read.
 */

import { useRef, useState } from "react";

import { useMe } from "../api/auth";
import { useMembers } from "../api/queries";
import { useCreateTask } from "../api/work";
import type { TaskPriority } from "../api/work";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import { Drawer } from "../components/ui";
import styles from "./Screen.module.css";

/** Mirrored from `collaboration.py`'s `MAY_WRITE`, like `Tasks.tsx`'s `MAY_CLOSE`. */
const MAY_ADD: readonly string[] = ["admin", "approver", "contributor"];

function mayAddTask(role: string | undefined): boolean | undefined {
  return role === undefined ? undefined : MAY_ADD.includes(role);
}

export function NewTask({ recordId }: { recordId?: string }) {
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("normal");
  const [owner, setOwner] = useState<string | null>(null);
  const create = useCreateTask();
  //: Two clicks inside one frame both read `isPending` as false; the ref is what stops the second.
  const sending = useRef(false);

  const mayAdd = mayAddTask(me?.role);
  const isAdmin = me?.role === "admin";
  // Asked for only by an Admin, and only once the form is open: nobody else may read the list.
  const members = useMembers(isAdmin && open);
  // No default for a read that has not answered: the picker offers yourself until it does, and says so
  // if it could not (below) - an empty list defaulted in would read as "nobody else is here".
  const people = members.data ? members.data.filter((member) => member.status === "active") : [];
  const ownerId = owner ?? me?.user_id;

  if (!mayAdd || !me) return null;

  function reset() {
    setTitle("");
    setDue("");
    setPriority("normal");
    setOwner(null);
    create.reset();
  }

  function close() {
    setOpen(false);
    reset();
  }

  function add() {
    const name = title.trim();
    if (!name || !ownerId || sending.current) return;
    sending.current = true;
    // A refusal is the mutation's own error, shown by `WriteFailed` below with the draft kept; a
    // success closes the form. The ref is released either way.
    create.mutate(
      {
        title: name,
        ownerId,
        priority,
        ...(recordId ? { recordId } : {}),
        ...(due ? { dueDate: due } : {}),
      },
      { onSuccess: close, onSettled: () => (sending.current = false) },
    );
  }

  return (
    <>
      <button type="button" className={styles.secondary} onClick={() => setOpen(true)}>
        New task
      </button>
      <Drawer
        open={open}
        onClose={close}
        title="New task"
        width="sm"
        testId="task-new-drawer"
        footer={
          <>
            <button
              type="button"
              className={styles.primary}
              disabled={!title.trim() || create.isPending}
              onClick={add}
            >
              {create.isPending ? "Saving…" : "Add task"}
            </button>
            <button type="button" className={styles.fixLink} onClick={close}>
              Cancel
            </button>
          </>
        }
      >
        <label className={styles.field}>
          <span>What needs doing?</span>
          <input
            aria-label="What needs doing?"
            value={title}
            maxLength={200}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>
        <label className={styles.field}>
          <span>Due</span>
          <input aria-label="Due" type="date" value={due} onChange={(event) => setDue(event.target.value)} />
        </label>
        <label className={styles.field}>
          <span>Priority</span>
          <select
            aria-label="Priority"
            value={priority}
            onChange={(event) => setPriority(event.target.value === "high" ? "high" : "normal")}
          >
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </select>
        </label>
        {isAdmin ? (
          <label className={styles.field}>
            <span>Assign to</span>
            <select
              aria-label="Assign to"
              value={ownerId ?? ""}
              onChange={(event) => setOwner(event.target.value)}
            >
              {/* Yourself first and always present, so the default is real before the list arrives. */}
              <option value={me.user_id}>{me.display_name ?? "You"} (you)</option>
              {people
                .filter((member) => member.user_id !== me.user_id)
                .map((member) => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.display_name}
                  </option>
                ))}
            </select>
          </label>
        ) : null}
        {isAdmin && members.error ? (
          <ReadFailed
            of="The list of people"
            error={members.error}
            onRetry={() => void members.refetch()}
          />
        ) : null}
        <WriteFailed error={create.error} of="that task" />
      </Drawer>
    </>
  );
}
